import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { User, Role, AssignRoleRequest } from '../../types';
import apiService from '../../services/api';

interface UserRoleDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess: () => void;
}

const UserRoleDialog: React.FC<UserRoleDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open) {
      fetchRoles();
      if (user?.roleId) {
        setSelectedRoleId(user.roleId);
      } else {
        setSelectedRoleId('');
      }
    }
  }, [open, user]);

  const fetchRoles = async () => {
    try {
      const rolesData = await apiService.getRoles();
      setRoles(rolesData.filter(role => role.isActive));
    } catch (err: any) {
      setError('Failed to fetch roles');
    }
  };

  const handleAssignRole = async () => {
    if (!user || selectedRoleId === '') return;

    setLoading(true);
    setError('');

    try {
      const assignData: AssignRoleRequest = {
        roleId: selectedRoleId as number,
      };
      await apiService.assignRoleToUser(user.id, assignData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      await apiService.removeRoleFromUser(user.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove role');
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = roles.find(role => role.id === selectedRoleId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage User Role - {user?.firstName} {user?.lastName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Current Role:
            </Typography>
            {user?.role ? (
              <Chip
                label={user.role.name}
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#16a34a',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No role assigned
              </Typography>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>Assign Role</InputLabel>
            <Select
              value={selectedRoleId}
              onChange={e => setSelectedRoleId(e.target.value as number | '')}
              label="Assign Role"
            >
              <MenuItem value="">
                <em>No role</em>
              </MenuItem>
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name} - {role.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedRole && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Role Permissions ({selectedRole.permissions?.length || 0}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedRole.permissions?.slice(0, 10).map(permission => (
                  <Chip
                    key={permission.id}
                    label={permission.name}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
                {(selectedRole.permissions?.length || 0) > 10 && (
                  <Chip
                    label={`+${(selectedRole.permissions?.length || 0) - 10} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {user?.roleId && (
            <Button onClick={handleRemoveRole} color="error" disabled={loading}>
              Remove Role
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={loading || selectedRoleId === ''}
            startIcon={loading && <CircularProgress size={16} />}
          >
            {selectedRoleId === user?.roleId ? 'Update Role' : 'Assign Role'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserRoleDialog;
